/**
 * Hosted (`*-url`): connect to the stag-ops server at <api host>/mcp
 * (deploy/ops-mcp). Local: run apps/ops-mcp from a stag checkout over stdio.
 */
export type McpClient =
  | 'claude-code-url'
  | 'claude-code-url-cli'
  | 'claude-code'
  | 'claude-code-cli'
  | 'claude-desktop';

export interface McpConfigOptions {
  client: McpClient;
  /** Investcore host the server calls, without a trailing slash or /api. */
  baseUrl: string;
  /** Personal admin token generated on the page; empty until generated. */
  token: string;
  readOnly: boolean;
}

export const SERVER_NAME = 'stag-ops';
const REPO_PATH = 'apps/ops-mcp/dist/index.js';
const DESKTOP_PATH = '/absolute/path/to/stag/apps/ops-mcp/dist/index.js';
export const TOKEN_ENV_REF = '${STAG_ADMIN_TOKEN}';

/** Strips trailing slashes and a trailing /api so the server can add its own paths. */
export const normalizeBaseUrl = (url: string) =>
  url.trim().replace(/\/+$/, '').replace(/\/api$/, '');

/** The hosted server's URL; `?readonly=true` asks it for the read-only tools only. */
export const mcpUrl = (baseUrl: string, readOnly: boolean) =>
  `${normalizeBaseUrl(baseUrl)}/mcp${readOnly ? '?readonly=true' : ''}`;

/**
 * Builds the MCP client config for apps/ops-mcp. The token is only written
 * into configs that stay on the user's machine (local Claude Code scope,
 * Claude Desktop); .mcp.json lives in the repo, so it reads the token from
 * the shell environment instead.
 */
export const buildMcpConfig = ({
  client,
  baseUrl,
  token,
  readOnly,
}: McpConfigOptions): string => {
  const value = token.trim();
  if (client === 'claude-code-url-cli') {
    const auth = `Authorization: Bearer ${value || TOKEN_ENV_REF}`;
    return `claude mcp add --transport http ${SERVER_NAME} --scope local \\\n  ${shellQuote(
      mcpUrl(baseUrl, readOnly)
    )} \\\n  --header ${value ? shellQuote(auth) : `"${auth}"`}`;
  }
  if (client === 'claude-code-url') {
    return JSON.stringify(
      {
        mcpServers: {
          [SERVER_NAME]: {
            type: 'http',
            url: mcpUrl(baseUrl, readOnly),
            headers: { Authorization: `Bearer ${TOKEN_ENV_REF}` },
          },
        },
      },
      null,
      2
    );
  }

  const desktop = client === 'claude-desktop';
  const local = client === 'claude-code-cli' || desktop;
  const env: Record<string, string> = {
    STAG_API_BASE_URL: normalizeBaseUrl(baseUrl),
    STAG_ADMIN_TOKEN:
      local && value ? value : desktop ? '<admin token>' : TOKEN_ENV_REF,
  };
  if (readOnly) env.STAG_OPS_MCP_READONLY = 'true';

  if (client === 'claude-code-cli') {
    const flags = Object.entries(env)
      .map(([key, v]) => `  -e ${key}=${shellQuote(v)} \\`)
      .join('\n');
    return `claude mcp add ${SERVER_NAME} --scope local \\\n${flags}\n  -- node ${REPO_PATH}`;
  }

  return JSON.stringify(
    {
      mcpServers: {
        [SERVER_NAME]: {
          command: 'node',
          args: [desktop ? DESKTOP_PATH : REPO_PATH],
          env,
        },
      },
    },
    null,
    2
  );
};

/** Env references stay double-quoted so the shell expands them; the rest is literal. */
const shellQuote = (value: string) => {
  if (/^\$\{\w+\}$/.test(value)) return `"${value}"`;
  if (/^[\w@%+=:,./-]+$/.test(value)) return value;
  return `'${value.replace(/'/g, `'\\''`)}'`;
};
